/**
 * @file PresetLibraryHost.tsx
 * @description Библиотека заготовок в конструкторе (phase19.16 Р2;
 * uiux-design-r2 §5): связывает окно `PresetLibraryDialog` с открытой
 * страницей — места вставки из её блоков, цвета ролей черновика, вставка
 * через `from-preset`, затем новый блок выбирается (канва подводится к нему
 * подсветкой), а у заготовки с фото-фоном сразу открывается выбор фото.
 *
 * Р2а (uiux-design-r2a §3): заготовки — только разрешённые в контексте
 * страницы (`sectionPresetsFor`: обычная страница, место, шаблон товара).
 * В шаблоне первой идёт группа «Страница товара» — три раскладки; раскладка
 * заменяет все блоки черновика шаблона (подтверждение, `useTemplateReset`).
 */

import { findProductTemplateLayout, sectionPresetsFor, SITE_SLOTS, type SectionPreset, type SiteSlotKey } from "@chaiberi/shared";

import { useSiteView } from "../../site-view/site-view-context";
import { blockTitle } from "../../site-pages/block-meta";
import { useBuilder, type LibraryOptions } from "../builder-context";
import { PresetLibraryDialog } from "./PresetLibraryDialog";
import { useTemplateReset } from "../../../hooks/site-builder/useTemplateReset";
import { SHOP_FEATURES } from "../../../lib/site-builder/shop-features";
import { sectionContextOf } from "../../../lib/site-builder/template-rules";

interface Props {
  library: ({ after: string | null | undefined } & LibraryOptions) | null;
  onClose: () => void;
}

/** «над лентой блога» — имя места для «Куда»; у обычной страницы нет. */
function slotPlaceName(slot: SiteSlotKey | null | undefined): string | undefined {
  const label = slot ? SITE_SLOTS.find((s) => s.key === slot)?.label : undefined;
  return label ? label.charAt(0).toLowerCase() + label.slice(1) : undefined;
}

export function PresetLibraryHost({ library, onClose }: Props) {
  const b = useBuilder();
  const view = useSiteView();
  const page = b.pages.page;
  const blocks = (page?.blocks ?? []).map((block) => ({ id: block.id, title: blockTitle(block) }));
  const context = sectionContextOf(page?.kind);
  const { reset } = useTemplateReset();

  async function insert(preset: SectionPreset, after: string | null | undefined): Promise<boolean> {
    // Раскладка страницы товара — замена всех блоков шаблона, а не вставка блока.
    const layout = preset.buildLayout ? findProductTemplateLayout(preset.key) : undefined;
    if (layout) return reset({ key: layout.key, title: layout.title });
    const id = await b.blocks.addPreset(preset, after);
    if (!id) return false;
    await b.select({ sel: { kind: "block", id }, panel: null });
    if (preset.backgroundPhoto) b.setBgPhotoRequest(id);
    return true;
  }

  return (
    <PresetLibraryDialog
      open={library !== null && page !== null}
      after={library?.after}
      firstGroup={library?.group ?? (context === "productTemplate" ? "product" : undefined)}
      presets={sectionPresetsFor(context)}
      placeName={slotPlaceName(page?.slot)}
      blocks={blocks}
      colors={view.parts?.theme.theme.colors ?? null}
      features={SHOP_FEATURES}
      onClose={onClose}
      onInsert={insert}
    />
  );
}
